import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { Values } from '@mode-switch/model';
import {
  EncChannel,
  EncField,
  EncSeries,
  Encoding,
  EncodingResult,
  MarkChannel,
  VariableType,
} from '@mode-vegas/vegas';
import { IconComponent } from '@mode/capra';
import { ChartFacade, DragFieldInfo, SelectedChartService } from '@mode/charts/contract';
import { EncodingHelpers, FieldFormatHelpers } from '@mode/charts/util-vegas';
import { BinSizeTypes, ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { AnalyticsEvent, AnalyticsService } from '@mode/shared/util-segment';
import { CompiledAttr, CompiledField, CompiledFormula } from '@mode/shared/util-switch';
import { getFormatMapKey } from 'libs/charts/util-vegas/src/lib/field-format-helpers';
import { Subject, takeUntil } from 'rxjs';
import { ChannelDropZoneComponent } from '../channel-drop-zone/channel-drop-zone.component';
import * as MarksHelpers from '../marks-helpers';

export type EncodingUpdateType =
  | 'dragEnd'
  | 'dropOnField'
  | 'dropOnEmpty'
  | 'typeAheadSelection'
  | 'fieldUpdate'
  | 'removeField'
  | 'removeMultiple';

@Component({
  standalone: true,
  imports: [ChannelDropZoneComponent, CommonModule, IconComponent],
  selector: 'mode-channel-component',
  templateUrl: './channel.component.html',
  styleUrls: ['./channel.component.scss'],
})
export class ChannelComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) channelSummary!: MarksHelpers.ChannelSummary;
  @Input({ required: true }) encoding!: Encoding;
  @Input({ required: true }) model!: FlamingoTypes.ModeModel;
  @Input({ required: true }) view!: FlamingoTypes.ModeView;
  @Input({ required: true }) chartService!: SelectedChartService;
  @Input({ required: true }) chartType!: ChartTypes.ChartType;
  @Input() series: EncSeries | false = false;
  @Input() queryToken: string | undefined;
  @Input() vizToken: string | undefined;
  @Input() allowScalars = true;
  @Input() allowAggregates = true;
  @Input() allowDiscrete = true;
  @Input() allowContinuous = true;
  @Input() allowBinned = true;
  @Input() requiresContinuous = false;
  @Input() showColorSettings = false;
  @Input() fieldFormatMap: ChartTypes.FieldFormatMap | undefined;
  @Input() format: ChartTypes.ChartFormat | undefined;
  @Input() binSizeToFieldMap: BinSizeTypes.BinSizeToFieldMap | undefined;
  @Input() useMeasureNamesPill = false;
  @Input() zone: EncodingHelpers.ZoneType | undefined;
  @Input() hideIcon = false;

  @Input() emitEncodingChanges = false;

  @Output() pendingEncodingUpdate = new EventEmitter<{
    change: EncodingUpdateType;
    encodingResult: EncodingResult;
    fieldFormatMap?: ChartTypes.FieldFormatMap;
  }>();

  icon: string | undefined;

  private dragInfo: DragFieldInfo | undefined;
  private destroyed$ = new Subject<void>();

  pendingBinSizeMap = new Map<
    string,
    {
      field: EncField;
      formula: CompiledField;
    }
  >();

  constructor(
    private analyticsService: AnalyticsService,
    private chartFacade: ChartFacade
  ) {
    this.chartFacade.dragField$.pipe(takeUntil(this.destroyed$)).subscribe((dragInfo: DragFieldInfo) => {
      this.dragInfo = dragInfo;
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    this.icon = !this.hideIcon ? MarksHelpers.IconChannelMap[this.channelSummary.channel] : undefined;
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onDragStart() {
    if (this.channelSummary.field != null) {
      const compiledFormula = CompiledField.fromEncField(this.view, this.channelSummary.field);
      const fieldIds = !this.series ? [this.channelSummary.id ?? ''] : undefined;
      this.chartFacade.startFieldDrag(compiledFormula, this.channelSummary.channel, fieldIds);
    }
  }

  onDragEnd() {
    if (!this.dragInfo?.formula || this.channelSummary.field?.id == null) {
      return;
    }
    let encResult: EncodingResult | undefined;
    if (this.series) {
      encResult = EncodingHelpers.removeFieldFromEncoding(this.encoding, this.chartType, this.channelSummary.field.id);
    } else {
      const fieldIds = this.channelSummary.fieldIds;
      encResult = EncodingHelpers.removeFieldsFromEncoding(this.encoding, fieldIds);
    }

    this.doDispatchEncodingUpdate('dragEnd', encResult);
    this.chartFacade.endFieldDrag();
  }

  onDropOnField() {
    const dragInfo: DragFieldInfo | undefined = this.dragInfo;
    const formula = dragInfo?.formula;

    this.chartService.endDrag();
    // Unclear what order of events would result in dragInfo.field being null, but it seems to be possible
    if (formula == null) {
      return;
    }
    const field = this.channelSummary.type === 'multiple' ? this.channelSummary.fieldIds : this.channelSummary.field;
    if (field == null) {
      return;
    }

    if (this.isSameField(formula, field)) {
      return;
    }

    let newEncoding: EncodingResult | undefined;
    const series = this.series || undefined;
    const channel = this.channelSummary.channel;
    const fieldIds =
      this.series || this.encoding.marks.channel === '*'
        ? (this.channelSummary.field?.id ?? '')
        : this.channelSummary.fieldIds;

    if (formula?.isField && dragInfo?.source && formula.alias) {
      const ids = this.getFieldIdorIds(
        dragInfo.source,
        formula.alias,
        series ? series : this.encoding.marks.series.length ? undefined : '*'
      );
      newEncoding = EncodingHelpers.replaceAndMoveVegasField(
        this.encoding,
        ids,
        channel,
        fieldIds,
        series ? series.field : this.encoding.marks.series.length ? undefined : '*',
        formula,
        this.zone
      );
    } else if (dragInfo?.formula) {
      newEncoding = EncodingHelpers.replaceAndAddNewVegasField(
        this.encoding,
        dragInfo.formula,
        fieldIds,
        channel,
        series ? series.field : this.encoding.marks.series.length ? undefined : '*',
        this.zone
      );
    }

    if (newEncoding != null) {
      this.doDispatchEncodingUpdate('dropOnField', newEncoding);
    }
  }

  onDropOnEmptyChannel() {
    const dragInfo: DragFieldInfo | undefined = this.dragInfo;
    const formula = dragInfo?.formula;
    const source = dragInfo?.source;

    this.chartService.endDrag();
    // Unclear what order of events would result in dragInfo.field being null, but it seems to be possible
    if (formula == null || source == null) {
      return;
    }

    const channel = this.channelSummary.channel;
    // no-op
    if (source === channel) {
      return;
    }

    let newEncoding: EncodingResult | undefined;
    const series = this.series ? this.series : undefined;

    if (formula.isField && formula.alias) {
      const ids = this.getFieldIdorIds(
        source,
        formula.alias,
        series ? series : this.encoding.marks.series.length ? undefined : '*'
      );
      newEncoding = EncodingHelpers.updateOrMoveVegasField(
        EncodingHelpers.loadEncoding(this.encoding),
        ids,
        channel,
        undefined,
        series ? series.field : this.encoding.marks.series.length ? undefined : '*',
        formula
      );
    } else {
      newEncoding = EncodingHelpers.addOrReplaceVegasField(
        EncodingHelpers.loadEncoding(this.encoding),
        formula,
        channel,
        series ? series.field : this.encoding.marks.series.length ? undefined : '*',
        this.zone
      );
    }

    if (newEncoding != null) {
      this.doDispatchEncodingUpdate('dropOnEmpty', newEncoding);

      this.analyticsService.track(AnalyticsEvent.Name.UiClicked, {
        location: AnalyticsEvent.SubContext.Chart,
        action: `Add field to ${channel} marks channel`,
        resource: AnalyticsEvent.Context.Visualization,
        chartType: this.chartType,
        channel,
      });
    }
  }

  onTypeAheadSelection($event: { attr: CompiledAttr; event: any; onFinalize: () => void }) {
    const { attr } = $event;
    const formula = attr ? attr : this.dragInfo?.formula;

    // Bail on empty formula
    if (formula == null) {
      return;
    }

    const newEncoding = EncodingHelpers.addOrReplaceVegasField(
      EncodingHelpers.loadEncoding(this.encoding),
      formula,
      this.channelSummary.channel,
      this.series ? this.series.field : this.encoding.marks.series.length ? undefined : '*',
      this.zone
    );
    if (newEncoding != null) {
      this.doDispatchEncodingUpdate('typeAheadSelection', newEncoding);
    }
  }

  onFieldUpdated($event: { $result: EncodingResult; $formats?: ChartTypes.FieldFormatMap }) {
    const { $result, $formats } = $event;
    this.doDispatchEncodingUpdate('fieldUpdate', $result, { ...(this.fieldFormatMap ?? {}), ...$formats });
  }

  onRemoveField($event: { $field: EncField | string[] }) {
    const { $field } = $event;
    let encResult: EncodingResult | undefined;
    if (Array.isArray($field)) {
      encResult = EncodingHelpers.removeFieldsFromEncoding(this.encoding, $field);
    } else if ($field.id) {
      encResult = EncodingHelpers.removeFieldFromEncoding(this.encoding, this.chartType, $field.id);
    }

    if (encResult) {
      this.doDispatchEncodingUpdate('removeField', encResult);
    }
  }

  onRemoveMultiple() {
    if (this.channelSummary.type !== 'multiple') {
      return;
    }
    const fieldIds = this.channelSummary.fieldIds;
    const encResult = EncodingHelpers.removeFieldsFromEncoding(this.encoding, fieldIds);
    this.doDispatchEncodingUpdate('removeMultiple', encResult);
  }

  onRemoveSort(source: string) {
    this.chartService.removeSort(source);
  }

  doDispatchEncodingUpdate(
    change: EncodingUpdateType,
    encodingResult: EncodingResult,
    fieldFormatMap?: ChartTypes.FieldFormatMap
  ) {
    if (this.emitEncodingChanges) {
      this.pendingEncodingUpdate.emit({ change, encodingResult, fieldFormatMap });
    } else {
      this.chartService.dispatchEncodingAndFormatUpdate(encodingResult, undefined, fieldFormatMap);
    }
  }

  onSetSplitLegend(isSplit: boolean): void {
    const measureValuesKey = getFormatMapKey(Values.Key, VariableType.CONTINUOUS);
    const measureValuesFieldFormat =
      this.fieldFormatMap?.[measureValuesKey] ?? FieldFormatHelpers.getDefaultFieldFormat();
    const legendFormat = { ...measureValuesFieldFormat.legendFormat, splitLegend: isSplit };
    // Update the legend format for Measure Values.
    this.chartService.updateLegendFormat(Values.Key, VariableType.CONTINUOUS, legendFormat);
  }

  getBinSize($event: { columnName: string; token: string }) {
    this.chartService.getBinSize($event.token, $event.columnName);
  }

  isSameField(formula: CompiledFormula | undefined, field: string[] | EncField): boolean {
    return formula?.isField && Array.isArray(field)
      ? field.some((id) => id === formula.alias)
      : formula?.isField && (field as EncField)?.id
        ? (field as EncField)?.id === formula.alias
        : false;
  }

  getFieldIdorIds(
    source: EncChannel | 'field-list',
    alias: string,
    series: EncSeries | '*' | undefined
  ): string | string[] {
    if (series === '*' || series?.field || !this.isMarkChannel(source)) {
      return alias;
    }
    const summary = EncodingHelpers.getSummary(this.encoding);
    const channelId = MarksHelpers.markChannelIdsKey(source as MarkChannel);
    return summary[channelId][alias];
  }

  isMarkChannel(channel: EncChannel | 'field-list') {
    return (
      channel !== 'row' &&
      channel !== 'column' &&
      channel !== 'value' &&
      channel !== 'filter' &&
      channel !== 'field-list'
    );
  }
}
